/**
 * Контракты события нажатия и метрик набора.
 *
 * KeystrokeEvent существует только на клиенте (в памяти и IndexedDB на время
 * сессии) и по умолчанию не отправляется на сервер: ТЗ §11.4, ARCHITECTURE §6.
 * Длительности измеряются монотонными часами (решение AD-013).
 */

import type { LayoutId } from './language.js';
import type { SessionId } from './ids.js';

/** Состояние модификаторов в момент нажатия. */
export interface ModifierState {
  readonly shift: boolean;
  readonly ctrl: boolean;
  readonly alt: boolean;
  readonly meta: boolean;
  readonly capsLock: boolean;
}

/** Модификаторы по умолчанию: ни один не нажат. */
export const NO_MODIFIERS: ModifierState = Object.freeze({
  shift: false,
  ctrl: false,
  alt: false,
  meta: false,
  capsLock: false,
});

/** Одно нажатие пользователя, нормализованное адаптером ввода. */
export interface KeystrokeEvent {
  /** Символ, который ожидался в текущей позиции. */
  readonly expectedCharacter: string;
  /** Символ, который пользователь фактически ввёл. */
  readonly actualCharacter: string;
  /** Монотонная метка времени, мс (performance.now()). */
  readonly occurredAtMonotonic: number;
  /** Интервал с момента предыдущего нажатия, мс. Для первого нажатия — 0. */
  readonly interKeyIntervalMs: number;
  /** Позиция в целевом тексте, с нуля. */
  readonly position: number;
  /** Является ли это нажатие исправлением ранее ошибочного символа. */
  readonly corrected: boolean;
  readonly modifierState: ModifierState;
  readonly layoutId: LayoutId;
  readonly sessionId: SessionId;
}

/** Тип ошибки после классификации (ТЗ §7.1, FR-EN-02). */
export const ERROR_KINDS = [
  /** Нажата соседняя клавиша. */
  'adjacent-key',
  /** Символ заменён на неблизкий. */
  'substitution',
  /** Символ пропущен. */
  'omission',
  /** Вставлен лишний символ. */
  'insertion',
  /** Две соседние буквы переставлены местами. */
  'transposition',
  /** Ошибка регистра: верный символ, неверный Shift. */
  'case',
  /** Ошибка раскладки: символ из другой раскладки. */
  'layout',
] as const;
export type ErrorKind = (typeof ERROR_KINDS)[number];

/** Зафиксированная ошибка ввода. */
export interface TypingError {
  readonly kind: ErrorKind;
  readonly expected: string;
  readonly actual: string;
  readonly position: number;
  /** Задержка перед ошибочным нажатием, мс. */
  readonly hesitationMs: number;
}

/** Метрики набора, вычисляемые на клиенте в реальном времени. */
export interface TypingMetrics {
  /** Слов в минуту: (символы / 5) / минуты. */
  readonly wpm: number;
  /** Символов в минуту. */
  readonly cpm: number;
  /**
   * Доля верных символов в получившемся тексте, 0..1.
   *
   * Это точность результата, а не набора: ошибка, стёртая Backspace, из неё
   * исчезает вместе с самим символом. Человек, исправивший всё, получает
   * единицу — текст у него действительно чистый.
   */
  readonly accuracy: number;
  /**
   * Доля нажатий, попавших с первого раза, 0..1.
   *
   * Это и есть точность набора. Она не прощает исправлений: двадцать
   * промахов, доведённых до правильного, оставляют её на прежнем месте, а
   * `accuracy` поднимают до единицы. Слепая печать измеряется именно ею
   * (ТЗ FR-EN-77).
   */
  readonly firstTryAccuracy: number;
  /**
   * Сколько нажатий сделано за прогон, включая стёртые потом.
   *
   * Отличается от `typedCharacters`: тот считает символы получившегося
   * текста, а Backspace их убирает. Нужен, чтобы складывать точность набора
   * нескольких прогонов без искажения.
   */
  readonly keystrokes: number;
  /** Количество введённых символов, включая ошибочные. */
  readonly typedCharacters: number;
  /** Количество верных символов. */
  readonly correctCharacters: number;
  /** Количество исправлений (Backspace). */
  readonly corrections: number;
  /** Стабильность ритма: 0..1, где 1 — идеально ровный темп. */
  readonly rhythmStability: number;
  /** Длительность по монотонным часам, мс. */
  readonly elapsedMs: number;
}

/** Пустые метрики — начальное состояние сессии. */
export const EMPTY_METRICS: TypingMetrics = Object.freeze({
  wpm: 0,
  cpm: 0,
  accuracy: 1,
  firstTryAccuracy: 1,
  keystrokes: 0,
  typedCharacters: 0,
  correctCharacters: 0,
  corrections: 0,
  rhythmStability: 0,
  elapsedMs: 0,
});

/** Количество символов, приравненных к одному условному слову (ТЗ §1.3). */
export const CHARACTERS_PER_WORD = 5;
